import type { ReactElement } from 'react';

import { Flex } from '@rango-dev/whale';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ProfileSummary } from '../../components/Profile';
import { LoggedInUserSidebar } from '../../components/Profile/LoggedInUserSidebar';
import { ProfileMobileMode } from '../../components/Profile/ProfileMobileMode';
import { ProfileUserData } from '../../components/Profile/ProfileUserData';
import { UserProfileNotFound } from '../../components/Profile/UserProfileNotFound';
import { SimpleNotification } from '../../components/SimpleNotification';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';
import {
  cancelProfileActions,
  dispatchCancellableAction,
  fetchProfileDetails,
  fetchProfileLeaderboard,
  fetchProfileSummary,
  resetMergeStatus,
  resetUserData,
  showPrivateProfileSelector,
} from '../../state/ProfileSlice';
import { profileSummarySelector } from '../../state/ProfileSlice/selectors';

function Profile(): ReactElement {
  const isLoggedIn = useAppSelector((state) => state.profile.isLoggedIn);
  const profileAuth = useAppSelector((state) => state.profile.profileAuth);
  const params = useParams<{ username: string }>();
  const profileSummary = useAppSelector((state) => profileSummarySelector(state, params.username));
  const profileDetails = useAppSelector((state) => state.profile.profileDetails);
  const accountMerged = useAppSelector((state) => state.profile.profileAuth.accountMerged);
  const showPrivateProfile = useAppSelector((state) =>
    showPrivateProfileSelector(state, params.username),
  );
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isDesktop } = useWindowDimensions();

  const metaTitle = t('Rango Exchange | {{username}} Rango Profile - Overall Score & Ranking', {
    username: params?.username || '',
  });
  const metaDescription = t(
    'View the Rango Exchange profile for {{username}} to explore their score, trading volume, and overall ranking.',
    { username: params?.username || 'username' },
  );

  useEffect(() => {
    if (params.username) {
      dispatchCancellableAction(dispatch, fetchProfileSummary, {
        username: params.username,
        public: !showPrivateProfile,
      });
      dispatchCancellableAction(dispatch, fetchProfileLeaderboard, { username: params.username });
    }

    return () => {
      cancelProfileActions();
      dispatch(resetUserData());
    };
  }, [params.username]);

  useEffect(() => {
    if (!profileAuth.loading && showPrivateProfile && !profileDetails.data) {
      dispatchCancellableAction(dispatch, fetchProfileDetails);
    }
  }, [profileAuth.loading, profileDetails.data?.id, showPrivateProfile]);

  const userNotFound = !profileSummary.loading && !profileSummary.data && !profileSummary.error;

  if (params.username && userNotFound) {
    return <UserProfileNotFound username={params.username} />;
  }

  return (
    <Flex
      direction={{ initial: 'column', sm: 'row' }}
      align={isLoggedIn ? 'start' : 'stretch'}
      gap={isLoggedIn ? '5' : { initial: '4', md: '5' }}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} />
        <meta name="twitter:title" content={metaTitle} />

        <meta name="description" content={metaDescription} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <SimpleNotification
        message={t('Something went wrong. Please try again.')}
        open={!!profileSummary.error || !!profileDetails.error}
        setOpen={() => {
          //
        }}
        severity="error"
      />
      <SimpleNotification
        message={t('Wallet added to your profile.')}
        open={accountMerged}
        setOpen={(open) => {
          if (!open) {
            dispatch(resetMergeStatus());
          }
        }}
        severity="success"
      />
      {isDesktop && <LoggedInUserSidebar />}

      {!isDesktop ? (
        <ProfileMobileMode />
      ) : (
        <Flex direction="column" gap="5" flexGrow="1">
          <ProfileSummary />
          {showPrivateProfile && <ProfileUserData />}
        </Flex>
      )}
    </Flex>
  );
}

export default Profile;
